import React, { useEffect, useState } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import { useTranslation } from "react-i18next"
import {
  Box,
  Paper,
  Grid,
  Typography,
  Divider,
  Skeleton,
  Avatar,
  Chip,
  Stack,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Breadcrumbs,
  ImageList,
  ImageListItem,
} from "@mui/material"
import EditIcon from "@mui/icons-material/Edit"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import DeleteIcon from "@mui/icons-material/Delete"
import InventoryIcon from "@mui/icons-material/Inventory"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import CategoryIcon from "@mui/icons-material/Category"
import LocalOfferIcon from "@mui/icons-material/LocalOffer"
import ScaleIcon from "@mui/icons-material/Scale"
import PercentIcon from "@mui/icons-material/Percent"
import BuildIcon from "@mui/icons-material/Build"
import ImageIcon from "@mui/icons-material/Image"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import NavigateNextIcon from "@mui/icons-material/NavigateNext"
import StraightenIcon from "@mui/icons-material/Straighten"
import Inventory2Icon from "@mui/icons-material/Inventory2"
import WarningIcon from "@mui/icons-material/Warning"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import ErrorIcon from "@mui/icons-material/Error"
import MiscellaneousServicesIcon from "@mui/icons-material/MiscellaneousServices"
import LayersIcon from "@mui/icons-material/Layers"
import ViewModuleIcon from "@mui/icons-material/ViewModule"
import AccessTimeIcon from "@mui/icons-material/AccessTime"
import AttachFileIcon from "@mui/icons-material/AttachFile"
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import ViewListIcon from "@mui/icons-material/ViewList"

import { getProducts, postProduct, deleteProduct, getOrders } from "../../helpers/api_helper"
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart"
import BoxDiagram from "../Store/BoxDiagram"

const FINISH_NAMES = {
  "1M": "MFC Melamine 18mm",
  "2P": "MFC + PVC Edge 18mm",
  "3S": "Synchron Effect",
  "4C": "Synchron 278cm",
  "5L": "MDF Satin Lacquer 19mm",
  "6D": "MDF Veneer Oak 20mm",
  "6L": "MDF Lacquer Satin 19-22mm",
  "7G": "MDF Gloss Lacquer 19mm",
  "7T": "MDF Special Frames 22mm",
  "8D": "Semi-Solid Oak 22mm",
  "8G": "Semi-Solid Oak Gloss 22mm",
  "9H": "Solid Oak Faltz 22mm",
  "9M": "Solid Oak 22mm",
  "10A": "Aluminum + Glass 20mm",
}

const MuiProductOverview = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [product, setProduct] = useState(null)
  const [requirements, setRequirements] = useState([])
  const [serviceTasks, setServiceTasks] = useState([])
  const [compiledItems, setCompiledItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [cloning, setCloning] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [selectedImage, setSelectedImage] = useState(null)
  const [totalSales, setTotalSales] = useState({ quantity: 0, value: 0 })

  // Price matrix filter state
  const [selPomolo, setSelPomolo] = useState(null)
  const [selModels, setSelModels] = useState(new Set())
  const [selKasoma, setSelKasoma] = useState(null)

  document.title = product
    ? `${product.name} | FlexFlow ERP`
    : t("Product Details") + " | FlexFlow ERP"

  useEffect(() => {
    if (id) {
      fetchProduct()
    }
  }, [id])

  const fetchProduct = async () => {
    setLoading(true)
    try {
      const data = await getProducts(id)
      if (data && data[0]) {
        setProduct(data[0])
        try {
          const reqs = JSON.parse(data[0].requirements || "[]")
          setRequirements(reqs)
        } catch (e) {
          setRequirements([])
        }
        try {
          const tasks = JSON.parse(data[0].serviceTasks || "[]")
          setServiceTasks(tasks)
        } catch (e) {
          setServiceTasks([])
        }
        try {
          const items = JSON.parse(data[0].compiledItems || "[]")
          setCompiledItems(items)
        } catch (e) {
          setCompiledItems([])
        }

        // Fetch total sales for this product
        fetchProductSales(parseInt(id))
      }
    } catch (error) {
      console.error("Error fetching product:", error)
    } finally {
      setLoading(false)
    }
  }

  const fetchProductSales = async (productId) => {
    try {
      const orders = await getOrders()
      let totalQuantity = 0
      let totalValue = 0

      if (orders && Array.isArray(orders)) {
        orders.forEach((order) => {
          try {
            const products = JSON.parse(order.products || "[]")
            products.forEach((item) => {
              if (item.id === productId) {
                const qty = parseFloat(item.quantity) || 0
                const price = parseFloat(item.salesprice || item.price) || 0
                totalQuantity += qty
                totalValue += qty * price
              }
            })
          } catch (e) {
            // Skip orders with invalid product data
          }
        })
      }

      setTotalSales({ quantity: totalQuantity, value: totalValue })
    } catch (error) {
      console.error("Error fetching sales data:", error)
    }
  }

  const handleCloneProduct = async () => {
    if (!product) return

    setCloning(true)
    const payload = {
      resource: [
        {
          name: `${product.name} (Copy)`,
          description: product.description,
          b2bsalesprice: product.b2bsalesprice,
          usageType: product.usageType,
          sku: `${product.sku}-copy-${Date.now()}`,
          vat: product.vat,
          salesprice: product.salesprice,
          vatClass: product.vatClass,
          unit: product.unit,
          stock: 0,
          category: product.category,
          requirements: product.requirements,
          productPhotos: product.productPhotos,
          serviceTasks: product.serviceTasks,
          productDepth: product.productDepth,
          productWidth: product.productWidth,
          productHeight: product.productHeight,
          productWeight: product.productWeight,
          packagingLength: product.packagingLength,
          packagingWidth: product.packagingWidth,
          packagingHeight: product.packagingHeight,
          packagingWeight: product.packagingWeight,
        },
      ],
    }

    try {
      const res = await postProduct(payload)
      if (res && res.resource && res.resource[0]) {
        navigate("/product-overview/" + res.resource[0].id)
      }
    } catch (error) {
      console.error("Error cloning product:", error)
    } finally {
      setCloning(false)
    }
  }

  const handleDeleteProduct = async () => {
    if (!product) return

    setDeleting(true)
    try {
      const payload = { resource: [{ id: product.id }] }
      await deleteProduct(payload)
      navigate("/products")
    } catch (error) {
      console.error("Error deleting product:", error)
    } finally {
      setDeleting(false)
      setDeleteDialogOpen(false)
    }
  }

  const getStockStatus = (stock) => {
    const stockLevel = Number(stock) || 0
    if (stockLevel <= 0) return { label: t("Out of Stock"), color: "#dc2626", icon: <ErrorIcon fontSize="small" /> }
    if (stockLevel < 10) return { label: t("Low Stock"), color: "#d97706", icon: <WarningIcon fontSize="small" /> }
    return { label: t("In Stock"), color: "#16a34a", icon: <CheckCircleIcon fontSize="small" /> }
  }

  // Parse product photos
  let productPhotos = []
  try {
    productPhotos = JSON.parse(product?.productPhotos || "[]")
  } catch (e) {
    productPhotos = []
  }

  // Loading skeleton
  if (loading) {
    return (
      <Box sx={{ px: 1.5, py: 1.5, bgcolor: "#f8fafc", minHeight: "100vh" }}>
        <Paper elevation={0} sx={{ p: 1.5, mb: 1.5, borderRadius: 2, border: "1px solid #e2e8f0" }}>
          <Skeleton width={300} height={32} />
        </Paper>
        <Grid container spacing={1.5}>
          <Grid item xs={12} lg={8}>
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: "1px solid #e2e8f0" }}>
              <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
            </Paper>
          </Grid>
          <Grid item xs={12} lg={4}>
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: "1px solid #e2e8f0" }}>
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
            </Paper>
          </Grid>
        </Grid>
      </Box>
    )
  }

  // No product found
  if (!product) {
    return (
      <Box sx={{ px: 1.5, py: 1.5, bgcolor: "#f8fafc", minHeight: "100vh" }}>
        <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: "1px solid #e2e8f0", textAlign: "center" }}>
          <InventoryIcon sx={{ fontSize: 64, color: "#94a3b8", mb: 2 }} />
          <Typography variant="h6" color="#1e293b" gutterBottom>
            {t("Product Not Found")}
          </Typography>
          <Typography variant="body2" color="#64748b" sx={{ mb: 3 }}>
            {t("The product you are looking for does not exist.")}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/products")}
            sx={{ bgcolor: "#3b82f6", textTransform: "none", borderRadius: 2 }}
          >
            {t("Back to Products")}
          </Button>
        </Paper>
      </Box>
    )
  }

  const stockStatus = getStockStatus(product.stock)

  return (
    <Box sx={{ px: 1.5, py: 1.5, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      {/* Compact Header */}
      <Paper elevation={0} sx={{ p: 1.5, mb: 1.5, borderRadius: 2, border: "1px solid #e2e8f0" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton size="small" onClick={() => navigate(-1)} sx={{ color: "#64748b" }}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" sx={{ color: "#94a3b8" }} />}>
              <Link to="/products" style={{ color: "#64748b", textDecoration: "none", fontSize: "0.875rem" }}>
                {t("Products")}
              </Link>
              <Typography color="#1e293b" fontSize="0.875rem" fontWeight={500}>
                {product.name}
              </Typography>
            </Breadcrumbs>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditIcon fontSize="small" />}
              onClick={() => navigate("/product-update", { state: { product } })}
              sx={{
                borderColor: "#3b82f6",
                color: "#3b82f6",
                textTransform: "none",
                borderRadius: 1.5,
                "&:hover": { borderColor: "#2563eb", bgcolor: "#eff6ff" },
              }}
            >
              {t("Edit")}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={cloning ? <CircularProgress size={14} /> : <ContentCopyIcon fontSize="small" />}
              onClick={handleCloneProduct}
              disabled={cloning}
              sx={{
                borderColor: "#e2e8f0",
                color: "#64748b",
                textTransform: "none",
                borderRadius: 1.5,
                "&:hover": { borderColor: "#cbd5e1", bgcolor: "#f8fafc" },
              }}
            >
              {t("Clone")}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DeleteIcon fontSize="small" />}
              onClick={() => setDeleteDialogOpen(true)}
              sx={{
                borderColor: "#fecaca",
                color: "#dc2626",
                textTransform: "none",
                borderRadius: 1.5,
                "&:hover": { borderColor: "#dc2626", bgcolor: "#fef2f2" },
              }}
            >
              {t("Delete")}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={1.5}>
        {/* Left Column */}
        <Grid item xs={12} lg={8}>
          {/* Product Info Card */}
          <Paper elevation={0} sx={{ p: 2.5, mb: 1.5, borderRadius: 2, border: "1px solid #e2e8f0" }}>
            <Grid container spacing={3}>
              {/* Product Image */}
              <Grid item xs={12} sm={4}>
                <Box
                  sx={{
                    width: "100%",
                    aspectRatio: "1",
                    bgcolor: "#f8fafc",
                    borderRadius: 2,
                    border: "1px solid #e2e8f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    cursor: productPhotos.length > 0 ? "pointer" : "default",
                  }}
                  onClick={() => productPhotos.length > 0 && setSelectedImage(productPhotos[0])}
                >
                  {productPhotos.length > 0 ? (
                    <img
                      src={productPhotos[0]}
                      alt={product.name}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (product.productDepth || product.productWidth || product.productHeight) ? (
                    <BoxDiagram
                      length={Number(product.productDepth) || 0}
                      height={Number(product.productHeight) || 0}
                      width={Number(product.productWidth) || 0}
                      size={180}
                      showLabels
                      productName={product.name}
                    />
                  ) : (
                    <InventoryIcon sx={{ fontSize: 64, color: "#cbd5e1" }} />
                  )}
                </Box>

                {/* Thumbnail gallery */}
                {productPhotos.length > 1 && (
                  <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                    {productPhotos.slice(0, 4).map((img, i) => (
                      <Box
                        key={i}
                        onClick={() => setSelectedImage(img)}
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: 1,
                          overflow: "hidden",
                          border: "2px solid #e2e8f0",
                          cursor: "pointer",
                          transition: "all 0.2s",
                          "&:hover": { borderColor: "#3b82f6" },
                        }}
                      >
                        <img src={img} alt={`Product ${i + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      </Box>
                    ))}
                    {productPhotos.length > 4 && (
                      <Box
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: 1,
                          bgcolor: "#f1f5f9",
                          border: "2px solid #e2e8f0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography variant="caption" color="#64748b" fontWeight={600}>
                          +{productPhotos.length - 4}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                )}
              </Grid>

              {/* Basic Info */}
              <Grid item xs={12} sm={8}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="h5" fontWeight={700} color="#1e293b">
                    {product.name}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                  <Chip
                    label={product.usageType?.replace("-", " ")}
                    size="small"
                    sx={{ bgcolor: "#dbeafe", color: "#1d4ed8", fontWeight: 500 }}
                  />
                  {product.category && (() => {
                    let cats = []
                    try {
                      const parsed = JSON.parse(product.category)
                      cats = Array.isArray(parsed) ? parsed : [product.category]
                    } catch {
                      cats = [product.category]
                    }
                    return cats.map((cat, idx) => (
                      <Chip
                        key={idx}
                        label={cat}
                        size="small"
                        variant="outlined"
                        sx={{ borderColor: "#e2e8f0", color: "#64748b" }}
                      />
                    ))
                  })()}
                  <Chip
                    icon={stockStatus.icon}
                    label={stockStatus.label}
                    size="small"
                    sx={{ bgcolor: `${stockStatus.color}15`, color: stockStatus.color, fontWeight: 500 }}
                  />
                </Stack>

                {product.description && (
                  <Typography variant="body2" color="#64748b" sx={{ mb: 2 }}>
                    {product.description}
                  </Typography>
                )}

                <Divider sx={{ my: 2, borderColor: "#f1f5f9" }} />

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <LocalOfferIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                      <Box>
                        <Typography variant="caption" color="#94a3b8">{t("SKU")}</Typography>
                        <Typography variant="body2" fontWeight={600} color="#1e293b">{product.sku}</Typography>
                      </Box>
                    </Stack>
                  </Grid>
                  <Grid item xs={6}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ScaleIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                      <Box>
                        <Typography variant="caption" color="#94a3b8">{t("Unit")}</Typography>
                        <Typography variant="body2" fontWeight={600} color="#1e293b">{product.unit}</Typography>
                      </Box>
                    </Stack>
                  </Grid>
                  {product.cabinet_position && (
                    <Grid item xs={6}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Inventory2Icon sx={{ fontSize: 18, color: "#94a3b8" }} />
                        <Box>
                          <Typography variant="caption" color="#94a3b8">{t("Position")}</Typography>
                          <Typography variant="body2" fontWeight={600} color="#1e293b">{product.cabinet_position}</Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  )}
                  {product.handle_style && (
                    <Grid item xs={6}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <BuildIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                        <Box>
                          <Typography variant="caption" color="#94a3b8">{t("Pomolo")}</Typography>
                          <Typography variant="body2" fontWeight={600} color="#1e293b">
                            {product.handle_style === "GOLA" ? "GOLA" : product.handle_style === "HANDLE" ? "OXI GOLA" : product.handle_style}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  )}
                  {product.cabinet_opening && (
                    <Grid item xs={6}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <ViewModuleIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                        <Box>
                          <Typography variant="caption" color="#94a3b8">{t("Opening")}</Typography>
                          <Typography variant="body2" fontWeight={600} color="#1e293b">{product.cabinet_opening}</Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  )}
                  {product.product_type && (
                    <Grid item xs={6}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CategoryIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                        <Box>
                          <Typography variant="caption" color="#94a3b8">{t("Type")}</Typography>
                          <Typography variant="body2" fontWeight={600} color="#1e293b">{product.product_type}</Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  )}
                  {product.provsku && (
                    <Grid item xs={6}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CategoryIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                        <Box>
                          <Typography variant="caption" color="#94a3b8">{t("Provider SKU")}</Typography>
                          <Typography variant="body2" fontWeight={600} color="#1e293b">{product.provsku}</Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Paper>

          {/* Product Variants — filter badges + price matrix */}
          {(() => {
            let parsedVariants = []
            try { parsedVariants = JSON.parse(product.variants || "[]") } catch { /* ignore */ }
            if (!Array.isArray(parsedVariants) || parsedVariants.length === 0) return null

            // Extract unique values
            const handleStyles = [...new Set(parsedVariants.map(v => v.handle_style).filter(Boolean))]
            const finishModels = []
            const fcSet = new Set()
            for (const v of parsedVariants) {
              if (v.finish_code && !fcSet.has(v.finish_code)) {
                fcSet.add(v.finish_code)
                finishModels.push(v.finish_code)
              }
            }
            finishModels.sort()
            const coreMaterials = [...new Set(parsedVariants.map(v => v.core_material).filter(Boolean))].sort()

            // Filter variants based on selections
            const filtered = parsedVariants.filter(v => {
              if (selPomolo && v.handle_style !== selPomolo) return false
              if (selKasoma && v.core_material !== selKasoma) return false
              if (selModels.size > 0 && !selModels.has(v.finish_code)) return false
              return true
            })

            // Build dimension × finish price grid from filtered
            const hasFilters = selPomolo || selKasoma || selModels.size > 0
            const dimMap = {}
            const activeFinishes = new Set()
            for (const v of filtered) {
              const key = `${v.width || 0}×${v.depth || 0}×${v.height || 0}`
              if (!dimMap[key]) dimMap[key] = { width: v.width, depth: v.depth, height: v.height, finishes: {} }
              const fc = v.finish_code || "-"
              activeFinishes.add(fc)
              if (!dimMap[key].finishes[fc]) dimMap[key].finishes[fc] = { std: v.salesprice, b2b: v.b2bsalesprice, sku: v.sku, stock: v.stock ?? 0 }
            }
            const dimKeys = Object.keys(dimMap).sort()
            const gridFinishes = [...activeFinishes].sort()

            const toggleModel = (code) => {
              setSelModels(prev => {
                const next = new Set(prev)
                if (next.has(code)) next.delete(code); else next.add(code)
                return next
              })
            }

            return (
              <Paper elevation={0} sx={{ p: 2.5, mb: 1.5, borderRadius: 2, border: "1px solid #e2e8f0" }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                  <Avatar sx={{ width: 36, height: 36, bgcolor: "#fce7f3", color: "#db2777" }}>
                    <ViewListIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
                      {t("Product Variants")}
                    </Typography>
                    <Typography variant="caption" color="#64748b">
                      {parsedVariants.length} {t("total")} {hasFilters ? `· ${filtered.length} ${t("filtered")}` : ""}
                    </Typography>
                  </Box>
                  {hasFilters && (
                    <Chip
                      label={t("Clear")}
                      size="small"
                      onClick={() => { setSelPomolo(null); setSelKasoma(null); setSelModels(new Set()) }}
                      sx={{ height: 22, fontSize: "0.65rem", fontWeight: 600, bgcolor: "#fee2e2", color: "#dc2626", cursor: "pointer" }}
                    />
                  )}
                </Stack>

                {/* Filter badges */}
                <Stack spacing={1.5} sx={{ mb: hasFilters ? 2 : 0 }}>
                  {/* Pomolo */}
                  {handleStyles.length > 0 && (
                    <Box>
                      <Typography variant="caption" fontWeight={600} color="#475569" sx={{ textTransform: "uppercase", mb: 0.5, display: "block", fontSize: "0.6rem" }}>
                        {t("Pomolo")}
                      </Typography>
                      <Stack direction="row" spacing={0.5}>
                        {handleStyles.map(hs => {
                          const label = hs === "GOLA" ? "GOLA" : "OXI GOLA"
                          const active = selPomolo === hs
                          return (
                            <Chip
                              key={hs}
                              label={label}
                              size="small"
                              variant={active ? "filled" : "outlined"}
                              onClick={() => setSelPomolo(active ? null : hs)}
                              sx={{
                                height: 24, fontSize: "0.7rem", fontWeight: 600, cursor: "pointer",
                                ...(active
                                  ? { bgcolor: hs === "GOLA" ? "#1d4ed8" : "#166534", color: "#fff", borderColor: "transparent" }
                                  : { borderColor: "#d1d5db", color: "#6b7280" }),
                              }}
                            />
                          )
                        })}
                      </Stack>
                    </Box>
                  )}

                  {/* Kasoma */}
                  {coreMaterials.length > 0 && (
                    <Box>
                      <Typography variant="caption" fontWeight={600} color="#475569" sx={{ textTransform: "uppercase", mb: 0.5, display: "block", fontSize: "0.6rem" }}>
                        {t("Kasoma")}
                      </Typography>
                      <Stack direction="row" spacing={0.5}>
                        {coreMaterials.map(cm => {
                          const active = selKasoma === cm
                          return (
                            <Chip
                              key={cm}
                              label={cm}
                              size="small"
                              variant={active ? "filled" : "outlined"}
                              onClick={() => setSelKasoma(active ? null : cm)}
                              sx={{
                                height: 24, fontSize: "0.7rem", fontWeight: 600, cursor: "pointer",
                                ...(active
                                  ? { bgcolor: "#d97706", color: "#fff", borderColor: "transparent" }
                                  : { borderColor: "#d1d5db", color: "#6b7280" }),
                              }}
                            />
                          )
                        })}
                      </Stack>
                    </Box>
                  )}

                  {/* Models */}
                  {finishModels.length > 0 && (
                    <Box>
                      <Typography variant="caption" fontWeight={600} color="#475569" sx={{ textTransform: "uppercase", mb: 0.5, display: "block", fontSize: "0.6rem" }}>
                        {t("Models")}
                      </Typography>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {finishModels.map(fc => {
                          const active = selModels.has(fc)
                          return (
                            <Chip
                              key={fc}
                              label={`${fc} — ${FINISH_NAMES[fc] || fc}`}
                              size="small"
                              variant={active ? "filled" : "outlined"}
                              onClick={() => toggleModel(fc)}
                              sx={{
                                height: 24, fontSize: "0.65rem", fontWeight: 500, cursor: "pointer",
                                ...(active
                                  ? { bgcolor: "#166534", color: "#fff", borderColor: "transparent" }
                                  : { borderColor: "#d1d5db", color: "#6b7280" }),
                              }}
                            />
                          )
                        })}
                      </Stack>
                    </Box>
                  )}
                </Stack>

                {/* Price grid — only shows when filters are active */}
                {hasFilters && filtered.length > 0 && (
                  <Box sx={{ overflowX: "auto", borderTop: "1px solid #e2e8f0", pt: 1.5 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: "#f8fafc" }}>
                          <TableCell sx={{ fontWeight: 700, fontSize: "0.65rem", color: "#475569", textTransform: "uppercase", py: 0.5 }}>
                            {t("W × D × H")}
                          </TableCell>
                          {gridFinishes.map(fc => (
                            <TableCell key={fc} align="center" sx={{ fontWeight: 700, fontSize: "0.65rem", color: "#166534", py: 0.5 }}>
                              {fc}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dimKeys.map(dk => {
                          const dim = dimMap[dk]
                          return (
                            <TableRow key={dk} sx={{ "&:hover": { bgcolor: "#f9fafb" } }}>
                              <TableCell sx={{ py: 0.5, whiteSpace: "nowrap" }}>
                                <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, fontFamily: "monospace", color: "#1e293b" }}>
                                  {dim.width} × {dim.depth} × {dim.height}
                                </Typography>
                              </TableCell>
                              {gridFinishes.map(fc => {
                                const p = dim.finishes[fc]
                                if (!p) return <TableCell key={fc} align="center" sx={{ py: 0.5 }}><Typography sx={{ fontSize: "0.7rem", color: "#d1d5db" }}>—</Typography></TableCell>
                                return (
                                  <TableCell key={fc} align="center" sx={{ py: 0.5 }}>
                                    {p.sku && (
                                      <Typography sx={{ fontSize: "0.55rem", fontFamily: "monospace", color: "#94a3b8", lineHeight: 1.2 }}>
                                        {p.sku}
                                      </Typography>
                                    )}
                                    <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: "#1e293b" }}>
                                      {p.std ? `€${Number(p.std).toFixed(0)}` : "—"}
                                    </Typography>
                                    {p.b2b && (
                                      <Typography sx={{ fontSize: "0.6rem", color: "#9ca3af" }}>
                                        €{Number(p.b2b).toFixed(0)}
                                      </Typography>
                                    )}
                                    <Typography sx={{ fontSize: "0.55rem", fontWeight: 600, color: p.stock > 0 ? "#16a34a" : "#ef4444", mt: 0.25 }}>
                                      stk: {p.stock || 0}
                                    </Typography>
                                  </TableCell>
                                )
                              })}
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                )}

                {hasFilters && filtered.length === 0 && (
                  <Typography variant="body2" color="#9ca3af" sx={{ textAlign: "center", py: 2 }}>
                    {t("No variants match the selected filters")}
                  </Typography>
                )}
              </Paper>
            )
          })()}

          {/* Requirements Card (hidden for SERVICE and COMPILED-PRODUCT type) */}
          {product.usageType !== "SERVICE" && product.usageType !== "COMPILED-PRODUCT" && (
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: "1px solid #e2e8f0" }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <Avatar sx={{ width: 36, height: 36, bgcolor: "#ede9fe", color: "#8b5cf6" }}>
                  <BuildIcon sx={{ fontSize: 18 }} />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
                    {t("Requirements / Materials")}
                  </Typography>
                  <Typography variant="caption" color="#64748b">
                    {t("Bill of materials for production")}
                  </Typography>
                </Box>
              </Stack>

              {requirements.length > 0 ? (
                <Stack spacing={2}>
                  {requirements.map((step, stepIndex) => (
                    <Paper key={stepIndex} variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: "#e2e8f0" }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                        <Avatar sx={{ bgcolor: "#3b82f6", width: 24, height: 24, fontSize: 12 }}>
                          {step.seq || stepIndex + 1}
                        </Avatar>
                        <Typography variant="subtitle2" fontWeight={600} color="#1e293b">
                          {step.typid || step.name || `Step ${stepIndex + 1}`}
                        </Typography>
                      </Stack>

                      {step.materials && step.materials.length > 0 ? (
                        <Stack spacing={1}>
                          {step.materials.map((material, matIndex) => (
                            <Stack
                              key={matIndex}
                              direction="row"
                              alignItems="center"
                              spacing={1.5}
                              sx={{ pl: 4 }}
                            >
                              <BuildIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
                              <Typography variant="body2" color="#475569" sx={{ flex: 1 }}>
                                {material.name}
                              </Typography>
                              <Chip
                                label={`${material.quantity} ${material.unit || ""}`}
                                size="small"
                                sx={{ bgcolor: "#f1f5f9", color: "#64748b" }}
                              />
                            </Stack>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="#94a3b8" sx={{ pl: 4 }}>
                          {t("No materials defined")}
                        </Typography>
                      )}
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <Box sx={{ textAlign: "center", py: 4, bgcolor: "#f8fafc", borderRadius: 2, border: "1px dashed #e2e8f0" }}>
                  <BuildIcon sx={{ fontSize: 40, color: "#cbd5e1", mb: 1 }} />
                  <Typography variant="body2" color="#94a3b8">
                    {t("No requirements defined for this product")}
                  </Typography>
                </Box>
              )}
            </Paper>
          )}

          {/* Compiled Items Card (only for COMPILED-PRODUCT type) */}
          {product.usageType === "COMPILED-PRODUCT" && (
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: "1px solid #e2e8f0" }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <Avatar sx={{ width: 36, height: 36, bgcolor: "#d1fae5", color: "#059669" }}>
                  <LayersIcon sx={{ fontSize: 18 }} />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
                    {t("Compiled Items")}
                  </Typography>
                  <Typography variant="caption" color="#64748b">
                    {compiledItems.length > 0
                      ? `${compiledItems.length} ${t("products/services")} · €${compiledItems.reduce((sum, item) => sum + (parseFloat(item.costprice) || 0) * (item.quantity || 1), 0).toFixed(2)} ${t("total")}`
                      : t("Products and services in this compilation")}
                  </Typography>
                </Box>
              </Stack>

              {compiledItems.length > 0 ? (
                <Stack spacing={1.5}>
                  {compiledItems.map((item, index) => (
                    <Paper
                      key={index}
                      variant="outlined"
                      sx={{ p: 2, borderRadius: 2, borderColor: "#e2e8f0" }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        {/* Product Photo */}
                        <Box
                          sx={{
                            width: 50,
                            height: 50,
                            borderRadius: 1.5,
                            bgcolor: "#f3f4f6",
                            overflow: "hidden",
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {item.photo ? (
                            <img
                              src={item.photo}
                              alt={item.name}
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                          ) : (
                            <ViewModuleIcon sx={{ fontSize: 20, color: "#9ca3af" }} />
                          )}
                        </Box>

                        {/* Product Info */}
                        <Box sx={{ flex: 1 }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="body2" fontWeight={600} color="#1e293b">
                              {item.name}
                            </Typography>
                            <Chip
                              label={item.usageType === "SERVICE" ? t("Service") : t("Product")}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: "0.65rem",
                                bgcolor: item.usageType === "SERVICE" ? "#ede9fe" : "#dcfce7",
                                color: item.usageType === "SERVICE" ? "#7c3aed" : "#166534",
                              }}
                            />
                          </Stack>
                          {(item.productDepth || item.productWidth || item.productHeight) && (
                            <Typography variant="caption" color="#64748b">
                              <StraightenIcon sx={{ fontSize: 11, mr: 0.5, verticalAlign: "middle" }} />
                              {item.productDepth || 0} × {item.productWidth || 0} × {item.productHeight || 0} cm
                            </Typography>
                          )}
                        </Box>

                        {/* Quantity & Price */}
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            label={`×${item.quantity || 1}`}
                            size="small"
                            sx={{ bgcolor: "#f1f5f9", color: "#64748b" }}
                          />
                          <Typography variant="body2" fontWeight={600} color="#059669">
                            €{((item.costprice || 0) * (item.quantity || 1)).toFixed(2)}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <Box sx={{ textAlign: "center", py: 4, bgcolor: "#f8fafc", borderRadius: 2, border: "1px dashed #e2e8f0" }}>
                  <LayersIcon sx={{ fontSize: 40, color: "#cbd5e1", mb: 1 }} />
                  <Typography variant="body2" color="#94a3b8">
                    {t("No items in this compilation")}
                  </Typography>
                </Box>
              )}
            </Paper>
          )}

          {/* Service Tasks Card (only for SERVICE type) */}
          {product.usageType === "SERVICE" && (
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: "1px solid #e2e8f0" }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <Avatar sx={{ width: 36, height: 36, bgcolor: "#ede9fe", color: "#8b5cf6" }}>
                  <MiscellaneousServicesIcon sx={{ fontSize: 18 }} />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
                    {t("Service Tasks")}
                  </Typography>
                  <Typography variant="caption" color="#64748b">
                    {serviceTasks.length > 0
                      ? `${serviceTasks.length} ${t("tasks")} · €${serviceTasks.reduce((sum, task) => sum + (parseFloat(task.price) || 0), 0).toFixed(2)} ${t("total")}`
                      : t("Tasks to be performed for this service")}
                  </Typography>
                </Box>
              </Stack>

              {serviceTasks.length > 0 ? (
                <Table size="small">
                  <TableBody>
                    {serviceTasks.map((task, index) => (
                      <TableRow key={index} hover sx={{ "&:hover": { bgcolor: "#f8fafc" } }}>
                        <TableCell sx={{ py: 1.5, border: "none" }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Avatar sx={{ width: 28, height: 28, bgcolor: "#ede9fe", color: "#8b5cf6", fontSize: 12 }}>
                              {index + 1}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={500} color="#1e293b">
                                {task.name}
                              </Typography>
                              {task.description && (
                                <Typography variant="caption" color="#64748b" sx={{ display: "block" }}>
                                  {task.description.substring(0, 60)}
                                  {task.description.length > 60 ? "..." : ""}
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ py: 1.5, border: "none" }}>
                          <Stack direction="row" spacing={0.5}>
                            {task.duration && (
                              <Chip
                                icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
                                label={`${task.duration} min`}
                                size="small"
                                sx={{ bgcolor: "#dbeafe", color: "#1d4ed8", borderRadius: 1, "& .MuiChip-icon": { color: "#1d4ed8" } }}
                              />
                            )}
                            {task.file && (
                              <Chip
                                icon={<AttachFileIcon sx={{ fontSize: 14 }} />}
                                label={t("File")}
                                size="small"
                                clickable
                                onClick={() => window.open(task.file, "_blank")}
                                sx={{ bgcolor: "#f3e8ff", color: "#7c3aed", borderRadius: 1, "& .MuiChip-icon": { color: "#7c3aed" } }}
                              />
                            )}
                            {task.photo && (
                              <Chip
                                icon={<PhotoCameraIcon sx={{ fontSize: 14 }} />}
                                label={t("Photo")}
                                size="small"
                                clickable
                                onClick={() => window.open(task.photo, "_blank")}
                                sx={{ bgcolor: "#fce7f3", color: "#db2777", borderRadius: 1, "& .MuiChip-icon": { color: "#db2777" } }}
                              />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ py: 1.5, border: "none", textAlign: "right" }}>
                          <Typography variant="body2" fontWeight={600} color="#16a34a">
                            €{parseFloat(task.price || 0).toFixed(2)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: "#f8fafc" }}>
                      <TableCell colSpan={2} sx={{ py: 1.5, borderTop: "1px solid #e2e8f0", borderBottom: "none" }}>
                        <Typography variant="body2" fontWeight={600} color="#475569">
                          {t("Service Total")}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.5, borderTop: "1px solid #e2e8f0", borderBottom: "none", textAlign: "right" }}>
                        <Typography variant="body1" fontWeight={700} color="#8b5cf6">
                          €{serviceTasks.reduce((sum, task) => sum + (parseFloat(task.price) || 0), 0).toFixed(2)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              ) : (
                <Box sx={{ textAlign: "center", py: 4, bgcolor: "#f8fafc", borderRadius: 2, border: "1px dashed #e2e8f0" }}>
                  <MiscellaneousServicesIcon sx={{ fontSize: 40, color: "#cbd5e1", mb: 1 }} />
                  <Typography variant="body2" color="#94a3b8">
                    {t("No service tasks defined for this product")}
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} lg={4}>
          {/* Stock & Sales Card */}
          <Paper elevation={0} sx={{ p: 2.5, mb: 1.5, borderRadius: 2, border: "1px solid #e2e8f0" }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
              <Avatar sx={{ width: 36, height: 36, bgcolor: "#dcfce7", color: "#16a34a" }}>
                <InventoryIcon sx={{ fontSize: 18 }} />
              </Avatar>
              <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
                {t("Inventory & Sales")}
              </Typography>
            </Stack>

            <Grid container spacing={2}>
              {/* Stock */}
              <Grid item xs={6}>
                <Box sx={{ textAlign: "center", py: 2, bgcolor: "#f8fafc", borderRadius: 2, border: "1px solid #e2e8f0" }}>
                  <Typography
                    variant="h3"
                    fontWeight={700}
                    sx={{ color: stockStatus.color }}
                  >
                    {product.stock || 0}
                  </Typography>
                  <Typography variant="body2" color="#64748b">
                    {t("Units in Stock")}
                  </Typography>
                  <Chip
                    icon={stockStatus.icon}
                    label={stockStatus.label}
                    size="small"
                    sx={{ mt: 1, bgcolor: `${stockStatus.color}15`, color: stockStatus.color, fontWeight: 500 }}
                  />
                </Box>
              </Grid>

              {/* Total Sales */}
              <Grid item xs={6}>
                <Box sx={{ textAlign: "center", py: 2, bgcolor: "#eff6ff", borderRadius: 2, border: "1px solid #dbeafe" }}>
                  <Typography
                    variant="h3"
                    fontWeight={700}
                    sx={{ color: "#3b82f6" }}
                  >
                    {totalSales.quantity}
                  </Typography>
                  <Typography variant="body2" color="#64748b">
                    {t("Units Sold")}
                  </Typography>
                  <Chip
                    icon={<ShoppingCartIcon sx={{ fontSize: 14 }} />}
                    label={`€${totalSales.value.toFixed(2)}`}
                    size="small"
                    sx={{ mt: 1, bgcolor: "#dbeafe", color: "#1d4ed8", fontWeight: 600, "& .MuiChip-icon": { color: "#1d4ed8" } }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>


        </Grid>
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, maxWidth: 400 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: "#fef2f2", color: "#dc2626" }}>
              <DeleteIcon />
            </Avatar>
            <Typography variant="h6" fontWeight={600}>
              {t("Delete Product")}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="#64748b">
            {t("Are you sure you want to delete")} <strong>{product.name}</strong>? {t("This action cannot be undone.")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ color: "#64748b", textTransform: "none" }}
          >
            {t("Cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleDeleteProduct}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
            sx={{
              bgcolor: "#dc2626",
              textTransform: "none",
              borderRadius: 2,
              "&:hover": { bgcolor: "#b91c1c" },
            }}
          >
            {deleting ? t("Deleting...") : t("Delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Lightbox */}
      <Dialog
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        maxWidth="lg"
        PaperProps={{ sx: { borderRadius: 2, bgcolor: "transparent", boxShadow: "none" } }}
      >
        <Box sx={{ position: "relative" }}>
          <IconButton
            onClick={() => setSelectedImage(null)}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              bgcolor: "rgba(0,0,0,0.5)",
              color: "white",
              "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
            }}
          >
            <DeleteIcon />
          </IconButton>
          <img
            src={selectedImage}
            alt="Product"
            style={{ maxWidth: "90vw", maxHeight: "90vh", borderRadius: 8 }}
          />
        </Box>
      </Dialog>
    </Box>
  )
}

export default MuiProductOverview
